import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { useSettings, logoUrl } from "../context/SettingsContext";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import GenderIcon from "../components/GenderIcon";
import { UserX, Plane, RefreshCw, Printer, Phone, Loader2 } from "lucide-react";

function today() { return new Date().toISOString().slice(0, 10); }
function monthStart() { return new Date().toISOString().slice(0, 8) + "01"; }

function StudentCard({ s, tone, onClick }) {
  const toneCls = tone === "leave" ? "border-amber-200 bg-amber-50" : "border-red-200 bg-red-50";
  return (
    <button onClick={onClick} data-testid={`absentee-card-${s.id}`}
      className={`surface-card w-full rounded-xl border ${toneCls} p-4 text-start transition-transform hover:-translate-y-0.5`}>
      <div className="flex items-center gap-2">
        <GenderIcon gender={s.gender} className="h-4 w-4" />
        <span className="font-medium text-foreground">{s.name}</span>
      </div>
      <div className="mt-1 text-xs text-muted-foreground">#{s.number} · {s.group}</div>
      {(s.phone1 || s.phone2) && (
        <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground" dir="ltr">
          <Phone className="h-3 w-3" /> {s.phone1 || s.phone2}
        </div>
      )}
    </button>
  );
}

export default function Absentees() {
  const { t, dir } = useLang();
  const { settings } = useSettings();
  const navigate = useNavigate();

  const [groups, setGroups] = useState([]);
  const [group, setGroup] = useState("all");

  // by-date state
  const [date, setDate] = useState(today());
  const [byDate, setByDate] = useState(null);
  const [loadingDate, setLoadingDate] = useState(false);
  const [auto, setAuto] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  // ranking state
  const [start, setStart] = useState(monthStart());
  const [end, setEnd] = useState(today());
  const [ranking, setRanking] = useState(null);
  const [loadingRank, setLoadingRank] = useState(false);

  const groupParam = useMemo(() => (group === "all" ? {} : { group_id: group }), [group]);

  useEffect(() => {
    api.get("/groups").then((r) => setGroups(r.data)).catch(() => {});
  }, []);

  const loadByDate = useCallback(async () => {
    setLoadingDate(true);
    try {
      const r = await api.get("/absentees/by-date", { params: { date, ...groupParam } });
      setByDate(r.data);
      setLastUpdated(new Date());
    } finally {
      setLoadingDate(false);
    }
  }, [date, groupParam]);

  const loadRanking = useCallback(async () => {
    setLoadingRank(true);
    try {
      const r = await api.get("/absentees/ranking", { params: { start, end, ...groupParam } });
      setRanking(r.data);
    } finally {
      setLoadingRank(false);
    }
  }, [start, end, groupParam]);

  useEffect(() => { loadByDate(); }, [loadByDate]);
  useEffect(() => { loadRanking(); }, [loadRanking]);

  // auto-refresh for the live by-date view
  useEffect(() => {
    if (!auto) return;
    const id = setInterval(loadByDate, 15000);
    return () => clearInterval(id);
  }, [auto, loadByDate]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4 no-print">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-foreground">{t("absentees.title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("absentees.subtitle")}</p>
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">{t("absentees.group")}</label>
          <Select value={group} onValueChange={setGroup}>
            <SelectTrigger className="w-[200px]" data-testid="absentees-group-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("absentees.allGroups")}</SelectItem>
              {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Tabs defaultValue="date" className="no-print">
        <TabsList data-testid="absentees-tabs">
          <TabsTrigger value="date" data-testid="tab-by-date">{t("absentees.byDate")}</TabsTrigger>
          <TabsTrigger value="rank" data-testid="tab-ranking">{t("absentees.ranking")}</TabsTrigger>
        </TabsList>

        {/* BY DATE */}
        <TabsContent value="date" className="space-y-5">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">{t("absentees.date")}</label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="absentees-date" className="w-[170px]" dir="ltr" />
            </div>
            <button onClick={loadByDate} data-testid="absentees-refresh" className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-secondary">
              <RefreshCw className={`h-4 w-4 ${loadingDate ? "animate-spin" : ""}`} /> {t("absentees.refresh")}
            </button>
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <Switch checked={auto} onCheckedChange={setAuto} data-testid="absentees-auto" /> {t("absentees.autoRefresh")}
            </label>
            {lastUpdated && (
              <span className="text-xs text-muted-foreground">{t("absentees.updated")}: {lastUpdated.toLocaleTimeString()}</span>
            )}
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-red-600">
                <UserX className="h-4 w-4" /> {t("absentees.absent")}
                <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-700" data-testid="absent-count">{byDate?.absent_count ?? 0}</span>
              </div>
              <div className="grid gap-3 sm:grid-cols-2" data-testid="absent-list">
                {byDate?.absent?.length ? byDate.absent.map((s) => (
                  <StudentCard key={s.id} s={s} tone="absent" onClick={() => navigate(`/student/${s.id}`)} />
                )) : <p className="text-sm text-muted-foreground">{t("absentees.noAbsent")}</p>}
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-amber-600">
                <Plane className="h-4 w-4" /> {t("absentees.leave")}
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700" data-testid="leave-count">{byDate?.leave_count ?? 0}</span>
              </div>
              <div className="grid gap-3 sm:grid-cols-2" data-testid="leave-list">
                {byDate?.leave?.length ? byDate.leave.map((s) => (
                  <StudentCard key={s.id} s={s} tone="leave" onClick={() => navigate(`/student/${s.id}`)} />
                )) : <p className="text-sm text-muted-foreground">{t("absentees.noLeave")}</p>}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* RANKING */}
        <TabsContent value="rank" className="space-y-5">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">{t("absentees.from")}</label>
              <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="rank-start" className="w-[170px]" dir="ltr" />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">{t("absentees.to")}</label>
              <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="rank-end" className="w-[170px]" dir="ltr" />
            </div>
            <button onClick={() => window.print()} data-testid="rank-print" className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-secondary">
              <Printer className="h-4 w-4" /> {t("absentees.print")}
            </button>
          </div>

          <div className="surface-card overflow-hidden rounded-xl border border-border bg-card">
            {loadingRank && <div className="flex items-center justify-center gap-2 py-10 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /></div>}
            {!loadingRank && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="ranking-table">
                  <thead>
                    <tr className="border-b border-border bg-secondary/60">
                      <th className="px-4 py-3 text-start font-medium text-muted-foreground">{t("absentees.number")}</th>
                      <th className="px-4 py-3 text-start font-medium text-muted-foreground">{t("absentees.student")}</th>
                      <th className="px-4 py-3 text-start font-medium text-muted-foreground">{t("absentees.group")}</th>
                      <th className="px-4 py-3 text-center font-medium text-red-600">{t("absentees.totalAbsent")}</th>
                      <th className="px-4 py-3 text-center font-medium text-amber-600">{t("absentees.totalLeave")}</th>
                      <th className="px-4 py-3 text-center font-medium text-muted-foreground">{t("absentees.percentage")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ranking?.students?.length ? ranking.students.map((s) => (
                      <tr key={s.id} className="border-b border-border last:border-0 hover:bg-secondary/40 cursor-pointer" data-testid={`ranking-row-${s.id}`} onClick={() => navigate(`/student/${s.id}`)}>
                        <td className="px-4 py-3 text-muted-foreground">{s.number}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 font-medium text-foreground"><GenderIcon gender={s.gender} className="h-3.5 w-3.5" /> {s.name}</div>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{s.group}</td>
                        <td className="px-4 py-3 text-center font-semibold text-red-600">{s.absent}</td>
                        <td className="px-4 py-3 text-center font-semibold text-amber-600">{s.leave}</td>
                        <td className="px-4 py-3 text-center font-semibold brand-text">{s.percentage}%</td>
                      </tr>
                    )) : (
                      <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">{t("absentees.noData")}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* print header */}
          <div id="print-report" className="hidden print:block">
            <div style={{ display: "flex", alignItems: "center", gap: 12, borderBottom: "2px solid #111", paddingBottom: 12, marginBottom: 14 }}>
              {logoUrl(settings.logo_path) ? <img src={logoUrl(settings.logo_path)} alt="logo" style={{ height: 48, width: 48, objectFit: "cover", borderRadius: 8 }} /> : null}
              <div>
                <div style={{ fontSize: 20, fontWeight: 700 }}>{settings.school_name}</div>
                <div style={{ fontSize: 12, color: "#444" }}>{t("absentees.title")} — {start} → {end}</div>
              </div>
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, direction: dir }}>
              <thead>
                <tr>
                  {[t("absentees.number"), t("absentees.student"), t("absentees.group"), t("absentees.totalAbsent"), t("absentees.totalLeave"), t("absentees.percentage")].map((h, i) => (
                    <th key={i} style={{ border: "1px solid #999", padding: 6, background: "#f0f0f0", textAlign: dir === "rtl" ? "right" : "left" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(ranking?.students || []).map((s) => (
                  <tr key={s.id}>
                    <td style={{ border: "1px solid #999", padding: 6 }}>{s.number}</td>
                    <td style={{ border: "1px solid #999", padding: 6 }}>{s.name}</td>
                    <td style={{ border: "1px solid #999", padding: 6 }}>{s.group}</td>
                    <td style={{ border: "1px solid #999", padding: 6, textAlign: "center" }}>{s.absent}</td>
                    <td style={{ border: "1px solid #999", padding: 6, textAlign: "center" }}>{s.leave}</td>
                    <td style={{ border: "1px solid #999", padding: 6, textAlign: "center" }}>{s.percentage}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
